/**
 * PassKey Management Component
 * 
 * Allows users to:
 * - Register new PassKeys (biometric/security key)
 * - View registered PassKeys
 * - Rename/delete PassKeys
 * - Generate and manage recovery codes
 * 
 * PassKey = WebAuthn credential = Inherently MFA (device + biometric)
 */

import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Fingerprint,
  Plus,
  Trash2,
  Pencil,
  Loader2,
  Shield,
  Smartphone,
  Key,
  CheckCircle2,
  AlertTriangle,
  Download,
  RefreshCw,
  Copy,
  Check,
  ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";

// Check if WebAuthn is supported
const isWebAuthnSupported = () => {
  return window.PublicKeyCredential !== undefined;
};

// Convert base64url to ArrayBuffer
const base64urlToBuffer = (base64url) => {
  const padding = "=".repeat((4 - (base64url.length % 4)) % 4);
  const base64 = (base64url + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray.buffer;
};

// Convert ArrayBuffer to base64url
const bufferToBase64url = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let str = "";
  for (const byte of bytes) {
    str += String.fromCharCode(byte);
  }
  return window.btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
};

export default function PassKeyManager({ api }) {
  const [passkeys, setPasskeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [showRegisterDialog, setShowRegisterDialog] = useState(false);
  const [deviceName, setDeviceName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  
  // Recovery codes state
  const [recoveryInfo, setRecoveryInfo] = useState(null);
  const [generatingCodes, setGeneratingCodes] = useState(false);
  const [showRecoveryDialog, setShowRecoveryDialog] = useState(false);
  const [newRecoveryCodes, setNewRecoveryCodes] = useState([]);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [savedConfirmed, setSavedConfirmed] = useState(false);

  const fetchPasskeys = useCallback(async () => {
    try {
      const res = await api().get("/auth/webauthn/credentials");
      setPasskeys(res.data.credentials || []);
    } catch (err) {
      console.error("Failed to fetch passkeys:", err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  const fetchRecoveryInfo = useCallback(async () => {
    try {
      const res = await api().get("/auth/webauthn/recovery-codes/count");
      setRecoveryInfo(res.data);
    } catch (err) {
      console.error("Failed to fetch recovery info:", err);
    }
  }, [api]);

  useEffect(() => {
    fetchPasskeys();
    fetchRecoveryInfo();
  }, [fetchPasskeys, fetchRecoveryInfo]);

  const handleRegister = async () => {
    if (!isWebAuthnSupported()) {
      toast.error("Your browser doesn't support PassKeys");
      return;
    }

    setRegistering(true);
    try {
      // Step 1: Get registration options from server
      const optionsRes = await api().post("/auth/webauthn/register/options", {
        device_name: deviceName || "PassKey",
      });

      const options = optionsRes.data.options;

      // Convert base64url strings to ArrayBuffers
      options.challenge = base64urlToBuffer(options.challenge);
      options.user.id = base64urlToBuffer(options.user.id);

      if (options.excludeCredentials) {
        options.excludeCredentials = options.excludeCredentials.map((cred) => ({
          ...cred,
          id: base64urlToBuffer(cred.id),
        }));
      }

      // Step 2: Create credential using browser API
      const credential = await navigator.credentials.create({
        publicKey: options,
      });

      // Step 3: Prepare credential for server
      const credentialForServer = {
        id: credential.id,
        rawId: bufferToBase64url(credential.rawId),
        type: credential.type,
        response: {
          clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
          attestationObject: bufferToBase64url(credential.response.attestationObject),
          transports: credential.response.getTransports?.() || ["internal"],
        },
      };

      // Step 4: Verify with server
      await api().post("/auth/webauthn/register/verify", {
        credential: credentialForServer,
        device_name: deviceName || "PassKey",
      });

      toast.success("PassKey registered successfully!");
      setShowRegisterDialog(false);
      setDeviceName("");
      fetchPasskeys();
    } catch (err) {
      console.error("Registration failed:", err);
      if (err.name === "NotAllowedError") {
        toast.error("Registration cancelled or timed out");
      } else if (err.name === "InvalidStateError") {
        toast.error("This device is already registered");
      } else {
        toast.error(err.response?.data?.detail || "Registration failed");
      }
    } finally {
      setRegistering(false);
    }
  };

  const handleRename = async (credentialId) => {
    if (!editName.trim()) return;
    
    try {
      await api().patch(`/auth/webauthn/credentials/${credentialId}`, {
        new_name: editName.trim(),
      });
      toast.success("PassKey renamed");
      setEditingId(null);
      setEditName("");
      fetchPasskeys();
    } catch (err) {
      toast.error("Failed to rename PassKey");
    }
  };

  const handleDelete = async (credentialId) => {
    try {
      await api().delete(`/auth/webauthn/credentials/${credentialId}`);
      toast.success("PassKey deleted");
      setDeletingId(null);
      fetchPasskeys();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete PassKey");
    }
  };

  const handleGenerateRecoveryCodes = async () => {
    setGeneratingCodes(true);
    try {
      const res = await api().post("/auth/webauthn/recovery-codes/generate");
      setNewRecoveryCodes(res.data.codes);
      setShowRecoveryDialog(true);
      setSavedConfirmed(false);
      fetchRecoveryInfo();
      toast.success("Recovery codes generated!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate recovery codes");
    } finally {
      setGeneratingCodes(false);
    }
  };

  const handleCopyCode = (code, index) => {
    navigator.clipboard.writeText(code);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const handleCopyAllCodes = () => {
    const allCodes = newRecoveryCodes.join("\n");
    navigator.clipboard.writeText(allCodes);
    toast.success("All codes copied to clipboard");
  };

  const handleDownloadCodes = () => {
    const content = `Niyant PassKey Recovery Codes
Generated: ${new Date().toISOString()}

IMPORTANT: Each code can only be used ONCE.
Store these codes securely - they will not be shown again.

${newRecoveryCodes.map((code, i) => `${i + 1}. ${code}`).join("\n")}

If you lose access to all your PassKey devices, use one of these codes at:
https://app.niyant.io/login (click "Account Recovery")
`;
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `niyant-recovery-codes-${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Recovery codes downloaded");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Never";
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]">
        <CardContent className="p-6">
          <div className="h-24 bg-[var(--n-border-2)] rounded animate-pulse" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]" data-testid="passkey-manager">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Fingerprint className="w-4 h-4 text-[var(--hig-accent)]" />
            PassKeys
            <Badge variant="outline" className="text-[10px] ml-2">Passwordless</Badge>
          </CardTitle>
          <Button
            size="sm"
            onClick={() => setShowRegisterDialog(true)}
            className="bg-[var(--hig-accent)] hover:bg-[var(--hig-accent)]/90 text-white text-xs h-8"
            disabled={!isWebAuthnSupported()}
            data-testid="add-passkey-btn"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Add PassKey
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Info Banner */}
        <div className="flex items-start gap-3 p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
          <Shield className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
              Phishing-Resistant Authentication
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              PassKeys use biometrics or security keys. No passwords to remember or steal.
            </p>
          </div>
        </div>

        {/* PassKey List */}
        {passkeys.length === 0 ? (
          <div className="py-8 text-center">
            <Key className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <h3 className="text-sm font-medium">No PassKeys registered</h3>
            <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto">
              Add a PassKey to sign in with biometrics instead of passwords.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {passkeys.map((pk) => (
              <div
                key={pk.credential_id}
                className="flex items-center gap-3 p-3 rounded-lg bg-[var(--n-bg)] border border-[var(--n-border-2)]"
                data-testid={`passkey-item-${pk.credential_id.slice(0, 8)}`}
              >
                <div className="p-2 rounded-lg bg-[var(--hig-accent)]/10">
                  <Smartphone className="w-4 h-4 text-[var(--hig-accent)]" />
                </div>
                <div className="flex-1 min-w-0">
                  {editingId === pk.credential_id ? (
                    <div className="flex items-center gap-2">
                      <Input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="h-7 text-xs"
                        placeholder="Device name"
                        autoFocus
                      />
                      <Button
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={() => handleRename(pk.credential_id)}
                      >
                        Save
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-xs"
                        onClick={() => {
                          setEditingId(null);
                          setEditName("");
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{pk.device_name}</span>
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Added {formatDate(pk.created_at)} · Last used {formatDate(pk.last_used_at)}
                      </p>
                    </>
                  )}
                </div>
                {editingId !== pk.credential_id && (
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={() => {
                        setEditingId(pk.credential_id);
                        setEditName(pk.device_name);
                      }}
                      data-testid={`edit-passkey-${pk.credential_id.slice(0, 8)}`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-red-500 hover:text-red-600 hover:bg-red-500/10"
                      onClick={() => setDeletingId(pk.credential_id)}
                      data-testid={`delete-passkey-${pk.credential_id.slice(0, 8)}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {!isWebAuthnSupported() && (
          <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
            <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-600 dark:text-amber-400">
                Browser Not Supported
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                PassKeys require a modern browser with WebAuthn support.
              </p>
            </div>
          </div>
        )}

        {/* Divider */}
        <div className="border-t border-[var(--n-border-2)] my-4" />

        {/* Recovery Codes Section */}
        <div className="space-y-3" data-testid="recovery-codes-section">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-amber-500" />
              <span className="text-sm font-medium">Recovery Codes</span>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={handleGenerateRecoveryCodes}
              disabled={generatingCodes}
              className="h-8 text-xs border-[var(--n-border-2)]"
              data-testid="generate-recovery-codes-btn"
            >
              {generatingCodes ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
              ) : (
                <RefreshCw className="w-3.5 h-3.5 mr-1" />
              )}
              {recoveryInfo?.has_recovery_codes ? "Regenerate" : "Generate"}
            </Button>
          </div>

          {recoveryInfo?.has_recovery_codes ? (
            <div className="p-3 rounded-lg bg-[var(--n-bg)] border border-[var(--n-border-2)]">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">
                    {recoveryInfo.remaining} of {recoveryInfo.total} codes remaining
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Generated on {recoveryInfo.generated_at ? new Date(recoveryInfo.generated_at).toLocaleDateString() : "N/A"}
                  </p>
                </div>
                {recoveryInfo.remaining <= 2 && (
                  <Badge variant="outline" className="text-amber-500 border-amber-500/30 bg-amber-500/5">
                    Low
                  </Badge>
                )}
              </div>
              {recoveryInfo.remaining <= 2 && (
                <p className="text-xs text-amber-500 mt-2">
                  Consider generating new codes soon.
                </p>
              )}
            </div>
          ) : (
            <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-600 dark:text-amber-400">
                    No Recovery Codes
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Generate recovery codes to regain access if you lose all your PassKey devices.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </CardContent>

      {/* Register Dialog */}
      <Dialog open={showRegisterDialog} onOpenChange={setShowRegisterDialog}>
        <DialogContent className="bg-[var(--n-card)] border-[var(--n-border-2)] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-sm">
              <Fingerprint className="w-4 h-4 text-[var(--hig-accent)]" />
              Register PassKey
            </DialogTitle>
            <DialogDescription className="text-[11px]">
              Use your device's biometrics or security key for passwordless sign-in.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-xs text-muted-foreground">Device Name (optional)</label>
              <Input
                value={deviceName}
                onChange={(e) => setDeviceName(e.target.value)}
                placeholder="e.g., MacBook Pro, iPhone"
                className="mt-1"
                data-testid="passkey-device-name-input"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowRegisterDialog(false)}
                className="flex-1 border-[var(--n-border-2)]"
              >
                Cancel
              </Button>
              <Button
                onClick={handleRegister}
                disabled={registering}
                className="flex-1 bg-[var(--hig-accent)] hover:bg-[var(--hig-accent)]/90 text-white"
                data-testid="confirm-register-passkey"
              >
                {registering ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Fingerprint className="w-4 h-4 mr-2" />
                )}
                {registering ? "Waiting..." : "Register"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deletingId} onOpenChange={(open) => !open && setDeletingId(null)}>
        <DialogContent className="bg-[var(--n-card)] border-[var(--n-border-2)] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-sm text-red-500">
              <AlertTriangle className="w-4 h-4" />
              Delete PassKey
            </DialogTitle>
            <DialogDescription className="text-[11px]">
              Are you sure you want to delete this PassKey? You won't be able to sign in with it anymore.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => setDeletingId(null)}
              className="flex-1 border-[var(--n-border-2)]"
            >
              Cancel
            </Button>
            <Button
              onClick={() => deletingId && handleDelete(deletingId)}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-passkey"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Recovery Codes Dialog */}
      <Dialog open={showRecoveryDialog} onOpenChange={(open) => {
        if (!open && !savedConfirmed) {
          // Warn user before closing
          if (!window.confirm("Have you saved your recovery codes? They will not be shown again.")) {
            return;
          }
        }
        setShowRecoveryDialog(open);
        if (!open) {
          setNewRecoveryCodes([]);
          setSavedConfirmed(false);
        }
      }}>
        <DialogContent className="bg-[var(--n-card)] border-[var(--n-border-2)] max-w-lg" data-testid="recovery-codes-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-sm">
              <Key className="w-4 h-4 text-[var(--hig-accent)]" />
              Your Recovery Codes
            </DialogTitle>
            <DialogDescription className="text-[11px]">
              Save these codes in a secure location. Each code can only be used once.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            {/* Warning Banner */}
            <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
              <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-600 dark:text-amber-400">
                  Save these codes now
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  These codes will NOT be shown again. Download or copy them before closing.
                </p>
              </div>
            </div>

            {/* Recovery Codes Grid */}
            <div className="grid grid-cols-2 gap-2">
              {newRecoveryCodes.map((code, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-2 rounded-md bg-[var(--n-bg)] border border-[var(--n-border-2)] font-mono text-sm"
                >
                  <span>{code}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={() => handleCopyCode(code, index)}
                  >
                    {copiedIndex === index ? (
                      <Check className="w-3.5 h-3.5 text-emerald-500" />
                    ) : (
                      <Copy className="w-3.5 h-3.5" />
                    )}
                  </Button>
                </div>
              ))}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleCopyAllCodes}
                className="flex-1 border-[var(--n-border-2)]"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy All
              </Button>
              <Button
                variant="outline"
                onClick={handleDownloadCodes}
                className="flex-1 border-[var(--n-border-2)]"
                data-testid="download-recovery-codes"
              >
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
            </div>

            {/* Confirmation Checkbox */}
            <div className="flex items-center gap-2 pt-2">
              <input
                type="checkbox"
                id="saved-confirmed"
                checked={savedConfirmed}
                onChange={(e) => setSavedConfirmed(e.target.checked)}
                className="rounded border-[var(--n-border-2)]"
              />
              <label htmlFor="saved-confirmed" className="text-xs text-muted-foreground">
                I have saved these recovery codes in a secure location
              </label>
            </div>

            {/* Close Button */}
            <Button
              onClick={() => setShowRecoveryDialog(false)}
              disabled={!savedConfirmed}
              className="w-full bg-[var(--hig-accent)] hover:bg-[var(--hig-accent)]/90 text-white"
              data-testid="close-recovery-dialog"
            >
              {savedConfirmed ? "Done" : "Save codes first"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
